import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAtomValue } from 'jotai'
import { Download, HelpCircle, Search } from 'lucide-react'
import './ScoreLogs.scss'
import Sidebar from '../../components/Sidebar/Sidebar'
import { drumtaleAdminSessionAtom } from '../../AtomStore'
import { openPageSpecification } from '../shared/pageSpecification'
import { Score, scoreCategories } from '../scores/scoresData'
import { scoreLogsDataModelGuide, scoreLogsImplementationGuide, scoreLogsSpecification } from './scoreLogsSpecification'

const TOAST_DURATION_MS = 2400

type PeriodMode = 'day' | 'week' | 'month'
type SortMode = 'popular' | 'low' | 'recent'

function matchesPeriod(value: string, start: string, end: string) {
  if (!value || value === '-') return false
  const dateText = value.slice(0, 10)
  return dateText >= (start || '1900-01-01') && dateText <= (end || '2999-12-31')
}

function ScoreLogs() {
  const navigate = useNavigate()
  const isAdminSessionActive = useAtomValue(drumtaleAdminSessionAtom)

  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('')
  const [sort, setSort] = useState<SortMode>('popular')
  const [periodStart, setPeriodStart] = useState('2026-07-01')
  const [periodEnd, setPeriodEnd] = useState('2026-07-28')

  const [toastMessage, setToastMessage] = useState('')
  const [isToastVisible, setIsToastVisible] = useState(false)
  const toastTimerRef = useRef<number | null>(null)

  useEffect(() => {
    if (!isAdminSessionActive) {
      navigate('/')
    }
  }, [isAdminSessionActive, navigate])

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current)
      }
    }
  }, [])

  const showToast = (message: string) => {
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current)
    }
    setToastMessage(message)
    setIsToastVisible(true)
    toastTimerRef.current = window.setTimeout(() => setIsToastVisible(false), TOAST_DURATION_MS)
  }

  const handlePeriodModeChange = (mode: PeriodMode) => {
    if (mode === 'week') {
      setPeriodStart('2026-07-22')
      setPeriodEnd('2026-07-28')
    } else if (mode === 'day') {
      setPeriodStart('2026-07-28')
      setPeriodEnd('2026-07-28')
    } else {
      setPeriodStart('2026-07-01')
      setPeriodEnd('2026-07-28')
    }
  }

  const handleOpenSpecification = () => {
    const opened = openPageSpecification(scoreLogsSpecification, scoreLogsDataModelGuide, scoreLogsImplementationGuide)
    if (!opened) {
      showToast('화면 사양서 창을 열 수 없습니다. 팝업 차단을 해제해 주세요.')
    }
  }

  const rows = useMemo(() => {
    return ([] as Score[])
      .filter((score) => !category || score.genre === category)
      .filter((score) => matchesPeriod(score.lastPlayed, periodStart, periodEnd))
      .filter((score) => !search || `${score.title} ${score.artist}`.toLowerCase().includes(search.toLowerCase()))
      .slice()
      .sort((a, b) => {
        if (sort === 'low') return a.plays - b.plays
        if (sort === 'recent') return b.lastPlayed.localeCompare(a.lastPlayed)
        return b.plays - a.plays
      })
  }, [search, category, sort, periodStart, periodEnd])

  const totalPlays = rows.reduce((sum, score) => sum + score.plays, 0)
  const genreSummary = useMemo(() => {
    const summary = new Map<string, number>()
    rows.forEach((score) => summary.set(score.genre, (summary.get(score.genre) || 0) + score.plays))
    return [...summary.entries()].sort((a, b) => b[1] - a[1])
  }, [rows])

  const handleDownload = () => {
    const headers = ['순위', '카테고리', '곡명', '가수명', '플레이 수', '최근 플레이']
    const csvRows = rows.map((score, index) => [index + 1, score.genre, score.title, score.artist, score.plays, score.lastPlayed])
    const csv = [headers, ...csvRows]
      .map((row) => row.map((value) => `"${String(value).replaceAll('"', '""')}"`).join(','))
      .join('\n')
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = '악보로그.csv'
    link.click()
    URL.revokeObjectURL(url)
    showToast('악보로그 엑셀 파일을 다운로드했습니다.')
  }

  return (
    <div className='ScoreLogs'>
      <div className='app-shell'>
        <Sidebar />

        <main className='workspace'>
          <header className='topbar'>
            <div className='title-area'>
              <h1>악보로그</h1>
            </div>
          </header>

          <section className='view active'>
            <div className='toolbar'>
              <div className='filter-group'>
                <label className='inline-search'>
                  <Search size={15} />
                  <input type='search' placeholder='곡명 또는 가수' value={search} onChange={(event) => setSearch(event.target.value)} />
                </label>
                <div className='period-range'>
                  <select className='filter period-filter' defaultValue='month' onChange={(event) => handlePeriodModeChange(event.target.value as PeriodMode)}>
                    <option value='day'>일간</option>
                    <option value='week'>주간</option>
                    <option value='month'>월간</option>
                  </select>
                  <input className='date-filter' type='date' value={periodStart} onChange={(event) => setPeriodStart(event.target.value)} />
                  <span>~</span>
                  <input className='date-filter' type='date' value={periodEnd} onChange={(event) => setPeriodEnd(event.target.value)} />
                </div>
                <select className='filter' value={category} onChange={(event) => setCategory(event.target.value)}>
                  <option value=''>카테고리 전체</option>
                  {scoreCategories.map((item) => (
                    <option key={item} value={item}>{item}</option>
                  ))}
                </select>
                <select className='filter' value={sort} onChange={(event) => setSort(event.target.value as SortMode)}>
                  <option value='popular'>인기순</option>
                  <option value='low'>플레이 낮은순</option>
                  <option value='recent'>최근 플레이순</option>
                </select>
              </div>
              <button className='secondary-button' type='button' onClick={handleDownload}>
                <Download size={15} />
                엑셀 다운로드
              </button>
            </div>

            <section className='panel'>
              <div className='panel-header'>
                <div>
                  <h2>악보별 플레이 로그</h2>
                  <p>카테고리별 곡 플레이 수를 리스트로 확인합니다.</p>
                </div>
                <span className='badge primary'>이번 달 기준</span>
              </div>
              <div className='table-wrap'>
                <table>
                  <thead>
                    <tr>
                      <th>순위</th>
                      <th>카테고리</th>
                      <th>곡</th>
                      <th>가수</th>
                      <th>플레이 수</th>
                      <th>최근 플레이</th>
                      <th>상세</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr><td colSpan={7}>검색 조건에 맞는 데이터가 없습니다.</td></tr>
                    ) : (
                      rows.map((score, index) => (
                        <tr key={score.title}>
                          <td>{index + 1}</td>
                          <td><span className='badge primary'>{score.genre}</span></td>
                          <td>
                            <div className='song-cell compact'>
                              <span className='thumb'>♪</span>
                              <div>
                                <strong>{score.title}</strong>
                                <small>{score.levels.join(', ')}</small>
                              </div>
                            </div>
                          </td>
                          <td>{score.artist}</td>
                          <td><strong>{score.plays.toLocaleString()}회</strong></td>
                          <td>{score.lastPlayed}</td>
                          <td>
                            <button className='secondary-button' type='button' onClick={() => showToast(`${score.title} 로그 상세는 추후 제공됩니다.`)}>상세</button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <div className='score-log-summary'>
              <article>
                <span>전체곡 플레이</span>
                <strong>{totalPlays.toLocaleString()}회</strong>
                <small>조회된 {rows.length}곡 합계</small>
              </article>
              {genreSummary.map(([genreName, plays]) => (
                <article key={genreName}>
                  <span>{genreName}</span>
                  <strong>{plays.toLocaleString()}회</strong>
                  <small>전체 {totalPlays ? Math.round((plays / totalPlays) * 100) : 0}%</small>
                </article>
              ))}
            </div>
          </section>
        </main>
      </div>

      <button
        className='page-spec-button'
        type='button'
        aria-label='현재 화면 사양서 열기'
        title='현재 화면 사양서'
        onClick={handleOpenSpecification}
      >
        <HelpCircle size={22} />
      </button>

      <div className={`toast${isToastVisible ? ' show' : ''}`} role='status' aria-live='polite'>
        {toastMessage}
      </div>
    </div>
  )
}

export default ScoreLogs
